/**
 * PLANES DE LECTURA — catálogo + avance. Las plantillas son ESTÁTICAS (offline,
 * derivadas de los metadatos de libros embarcados); lo único persistido y
 * sincronizado es el progreso (`bible_reading_progress`, privado por usuario).
 */
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { createMemo, For, Show } from "solid-js";
import { CalendarCheck2, ChevronRight, ListChecks } from "lucide-solid";
import { Link } from "@/lib/u-router";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { READING_PLANS } from "@/features/reading-plans/data/plan-templates";
import { computePlanProgress } from "@/features/reading-plans/logic";
import { useReadingProgress } from "@/features/reading-plans/hooks/use-reading-progress";

export const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans",
  component: PlansPage,
});

const PERCENT_MAX = 100;

function PlansPage() {
  const { t } = useI18n();
  const { activeRows } = useReadingProgress();

  const progressByPlan = createMemo(() => {
    const rows = activeRows();
    return new Map(
      READING_PLANS.map((plan) => [plan.id, computePlanProgress(rows, plan.id, plan.days.length)]),
    );
  });

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-6" data-testid="plans-page">
        <PageHeader
          icon={ListChecks}
          title={t(msg`Reading plans`)}
          description={t(msg`Guided journeys through Scripture — fully offline, at your pace.`)}
        />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 pb-24">
          <For each={READING_PLANS}>
            {(plan) => {
              const view = () => progressByPlan().get(plan.id);
              return (
                <Link
                  to="/plans/$planId"
                  params={{ planId: plan.id }}
                  class="group block"
                  data-testid={`plan-card-${plan.id}`}
                >
                  <Card class="h-full border-border/35 group-hover:border-primary/40 transition-colors">
                    <CardContent class="pt-5 space-y-3">
                      <div class="flex items-start justify-between gap-3">
                        <h3 class="text-sm font-extrabold text-foreground/90 font-serif leading-snug">
                          {t(plan.title)}
                        </h3>
                        <ChevronRight class="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <p class="text-xs text-muted-foreground leading-relaxed">
                        {t(plan.description)}
                      </p>
                      <div class="space-y-1.5">
                        <div class="flex items-center justify-between text-3xs font-bold text-muted-foreground">
                          <span class="flex items-center gap-1">
                            <CalendarCheck2 class="w-3 h-3" />
                            {view()?.completedCount ?? 0} / {plan.days.length} {t(msg`days`)}
                          </span>
                          <span data-testid={`plan-percent-${plan.id}`}>
                            {view()?.percent ?? 0}%
                          </span>
                        </div>
                        <div class="h-2 w-full rounded-full bg-secondary/25 overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500"
                            style={{
                              width: `${Math.min(view()?.percent ?? 0, PERCENT_MAX)}%`,
                            }}
                          />
                        </div>
                        <Show when={view()?.isComplete}>
                          <p class="text-3xs font-bold text-primary">{t(msg`Plan completed!`)}</p>
                        </Show>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            }}
          </For>
        </div>
      </div>
    </PageContainer>
  );
}
